<template>
  <div class="hr-ts-attachment-row">
    <!-- Remote URL with no custom actions: keep a single outbound link. -->
    <a
      v-if="isRemote && !$slots.actions"
      :href="attachment.filePath"
      target="_blank"
      rel="noopener noreferrer"
      class="hr-ts-attachment-row__link"
      :title="attachment.fileName"
      @click.stop
    >
      <i class="bi bi-file-earmark" aria-hidden="true"></i>
      <span class="hr-ts-attachment-row__name">{{ attachment.fileName }}</span>
      <span class="hr-ts-attachment-row__size">({{ formattedSize }})</span>
    </a>
    <!-- Stored file, or remote when parent supplies #actions (e.g. manager Edit/Delete). -->
    <div v-else class="hr-ts-attachment-row__stored">
      <span class="hr-ts-attachment-row__label">
        <a
          v-if="isRemote"
          :href="attachment.filePath"
          target="_blank"
          rel="noopener noreferrer"
          class="hr-ts-attachment-row__link hr-ts-attachment-row__link--inline"
          :title="attachment.fileName"
          @click.stop
        >
          <i class="bi bi-file-earmark" aria-hidden="true"></i>
          <span class="hr-ts-attachment-row__name">{{ attachment.fileName }}</span>
          <span class="hr-ts-attachment-row__size">({{ formattedSize }})</span>
        </a>
        <template v-else>
          <button
            v-if="clickableStoredName"
            type="button"
            class="hr-ts-attachment-row__name-btn hr-ts-attachment-row__link hr-ts-attachment-row__link--inline"
            :title="attachment.fileName"
            @click.prevent="$emit('open-stored', attachment)"
          >
            <i class="bi bi-file-earmark" aria-hidden="true"></i>
            <span class="hr-ts-attachment-row__name">{{ attachment.fileName }}</span>
            <span class="hr-ts-attachment-row__size">({{ formattedSize }})</span>
          </button>
          <template v-else>
            <i class="bi bi-file-earmark" aria-hidden="true"></i>
            <span class="hr-ts-attachment-row__name" :title="attachment.fileName">{{ attachment.fileName }}</span>
            <span class="hr-ts-attachment-row__size">({{ formattedSize }})</span>
          </template>
        </template>
      </span>
      <div v-if="$slots.actions" class="hr-ts-attachment-row__actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<script>
import { isRemoteHRTimesheetAttachmentUrl, formatHRTimesheetFileSize } from '@/utils/hrTimesheetAttachment';

export default {
  name: 'HRTimesheetAttachmentRow',
  emits: ['open-stored'],
  props: {
    attachment: {
      type: Object,
      required: true
    },
    /** When true, non-remote files open via open-stored (manager View / auth blob flow). */
    clickableStoredName: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isRemote() {
      return isRemoteHRTimesheetAttachmentUrl(this.attachment?.filePath);
    },
    formattedSize() {
      return formatHRTimesheetFileSize(this.attachment?.fileSize);
    }
  }
};
</script>

<style scoped>
.hr-ts-attachment-row {
  min-width: 0;
  max-width: 100%;
}

.hr-ts-attachment-row__link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 100%;
  min-width: 0;
  color: #3b82f6;
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 500;
  transition: color 0.2s ease;
}

.hr-ts-attachment-row__link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.hr-ts-attachment-row__link--inline {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  flex: 1 1 auto;
}

.hr-ts-attachment-row__link i {
  font-size: 0.875rem;
  flex-shrink: 0;
}

.hr-ts-attachment-row__name {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hr-ts-attachment-row__size {
  color: #9ca3af;
  font-size: 0.625rem;
  margin-left: 0.25rem;
  flex-shrink: 0;
}

.hr-ts-attachment-row__stored {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  font-size: 0.75rem;
  width: 100%;
  min-width: 0;
}

.hr-ts-attachment-row__label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;
  color: #374151;
  font-weight: 500;
}

.hr-ts-attachment-row__label > .bi {
  flex-shrink: 0;
}

.hr-ts-attachment-row__actions {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.hr-ts-attachment-row__name-btn {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
</style>
